<template>
  <modal
    elem-id="modalContatoFidelizacao"
    size="small"
    :title="t('Registrar Contato - Fidelização')"
    :close-label="t('Fechar')"
    :save-label="t('Salvar')"
    :save-disabled-fn="() => !formRef?.canSave"
    :save-fn="handleSave"
    :show="isVisible"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
        <contato-fidelizacao-form
        ref="formRef"
        :is-edit-mode="isEditMode"
        @save-success="handleSaveSuccess">
      </contato-fidelizacao-form>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useContatoFidelizacao } from '../../composables/useContatoFidelizacao';
import { useConfirmAction } from '../../composables/useConfirmAction';
import ContatoFidelizacaoForm from './ContatoFidelizacaoForm.vue';

type Id = number | string;

interface ContatoFormInstance {
  canSave: boolean;
  initializeForNew: (franquiaId: Id | null | undefined, clienteId: Id | null | undefined, clienteNome: string | null | undefined, franquiaNome: string | null | undefined) => Promise<void>;
  loadForEdit: (contatoId: Id) => Promise<void>;
  saveContato: () => Promise<void>;
  resetForm: () => void;
}

const { t } = useI18n();

const { deleteContatoFidelizacao } = useContatoFidelizacao();
const { confirmAction } = useConfirmAction();

const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const isDeleting = ref<boolean>(false);
const contatoId = ref<Id | null>(null);
const formRef = ref<ContatoFormInstance | null>(null);

const currentFranquiaId = ref<Id | null>(null);
const currentClienteId = ref<Id | null>(null);
const currentClienteNome = ref<string>('');
const currentFranquiaNome = ref<string>('');

const openForNew = async (
  franquiaId: Id | null | undefined,
  clienteId: Id | null | undefined,
  clienteNome: string | null | undefined,
  franquiaNome: string | null | undefined
): Promise<void> => {
  log('ContatoFidelizacaoModal', 'openForNew', 'Called with params', { franquiaId, clienteId, clienteNome, franquiaNome });
  
  isEditMode.value = false;
  contatoId.value = null;
  log('ContatoFidelizacaoModal', 'openForNew', 'Set isEditMode to false');
  
  currentFranquiaId.value = franquiaId ?? null;
  currentClienteId.value = clienteId ?? null;
  currentClienteNome.value = clienteNome ?? '';
  currentFranquiaNome.value = franquiaNome ?? '';
  log('ContatoFidelizacaoModal', 'openForNew', 'Stored current context');
  
  // Show modal first
  log('ContatoFidelizacaoModal', 'openForNew', 'Setting isVisible to true');
  isVisible.value = true;
  log('ContatoFidelizacaoModal', 'openForNew', 'isVisible is now', isVisible.value);
  
  // Wait for next tick to ensure form is mounted
  await nextTick();
  log('ContatoFidelizacaoModal', 'openForNew', 'After nextTick, formRef.value', formRef.value);
  
  // Initialize form for new contact
  if (formRef.value) {
    log('ContatoFidelizacaoModal', 'openForNew', 'Form ref available, initializing form');
    await formRef.value.initializeForNew(franquiaId, clienteId, clienteNome, franquiaNome);
    log('ContatoFidelizacaoModal', 'openForNew', 'Form initialized successfully');
  } else {
    log('ContatoFidelizacaoModal', 'openForNew', 'WARNING: Form ref NOT available');
  }
  
  log('ContatoFidelizacaoModal', 'openForNew', 'Modal opened - completed');
};

const openForEdit = async (contatoIdToEdit: Id): Promise<void> => {
  log('ContatoFidelizacaoModal', 'openForEdit', 'Begin', contatoIdToEdit);

  isEditMode.value = true;
  contatoId.value = contatoIdToEdit;

  isVisible.value = true;

  await nextTick();

  if (formRef.value) {
    log('ContatoFidelizacaoModal', 'openForEdit', 'Loading contact data');
    await formRef.value.loadForEdit(contatoIdToEdit);
  } else {
    log('ContatoFidelizacaoModal', 'openForEdit', 'Form ref not available yet');
  }

  log('ContatoFidelizacaoModal', 'openForEdit', 'Modal opened');
};

const handleSave = async (): Promise<void> => {
  log('ContatoFidelizacaoModal', 'handleSave', 'Save button clicked');
  if (formRef.value) {
    log('ContatoFidelizacaoModal', 'handleSave', 'Calling form saveContato');
    await formRef.value.saveContato();
  } else {
    log('ContatoFidelizacaoModal', 'handleSave', 'Form ref is null');
  }
};

const handleSaveSuccess = (savedData: unknown): void => {
  log('ContatoFidelizacaoModal', 'handleSaveSuccess', 'Contact saved', savedData);
  isVisible.value = false;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

const handleDelete = (): void => {
  log('ContatoFidelizacaoModal', 'handleDelete', 'Delete button clicked', contatoId.value);

  if (!contatoId.value) {
    log('ContatoFidelizacaoModal', 'handleDelete', 'No contact ID');
    return;
  }

  confirmAction({
    message: t('Tem certeza que deseja excluir este contato?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        isDeleting.value = true;
        log('ContatoFidelizacaoModal', 'handleDelete', 'Deleting contact', contatoId.value);

        await deleteContatoFidelizacao(Number(contatoId.value));

        log('ContatoFidelizacaoModal', 'handleDelete', 'Deleted successfully');
        eventBus.emit('globalMessage.success', t('Contato excluído com sucesso!'));
        eventBus.emit('contato-fidelizacao.deleted');

        isVisible.value = false;

        // Reset form
        if (formRef.value) {
          formRef.value.resetForm();
        }

      } catch (error) {
        log('ContatoFidelizacaoModal', 'handleDelete', 'Error deleting contato', error);
        const message = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', message);
      } finally {
        isDeleting.value = false;
        log('ContatoFidelizacaoModal', 'handleDelete', 'Finished');
      }
    }
  });
};

const handleClose = (): void => {
  log('ContatoFidelizacaoModal', 'handleClose', 'Modal closing');
  isVisible.value = false;

  if (formRef.value) {
    formRef.value.resetForm();
  }
};

interface OpenNewPayload {
  franquia_id?: Id;
  cliente_id?: Id;
  cliente_nome?: string;
  franquia_nome?: string;
}

interface OpenEditPayload {
  contatofidelizacao_id: Id;
}

interface ModalOpenPayload {
  id?: Id;
  franquiaId?: Id;
  clienteId?: Id;
  clienteNome?: string;
  franquiaNome?: string;
}

const handleOpenModalNew = (payload?: OpenNewPayload): void => {
  log('ContatoFidelizacaoModal', 'eventBus:open-new', 'EVENT RECEIVED "contato-fidelizacao.open-modal-new"', payload);
  if (!payload) return;
  openForNew(
    payload.franquia_id,
    payload.cliente_id,
    payload.cliente_nome,
    payload.franquia_nome
  );
};

const handleOpenModalEdit = (payload?: OpenEditPayload): void => {
  log('ContatoFidelizacaoModal', 'eventBus:open-edit', 'EVENT RECEIVED "contato-fidelizacao.open-modal-edit"', payload);
  if (!payload) return;
  openForEdit(payload.contatofidelizacao_id);
};

const handleModalOpen = async (data: ModalOpenPayload | undefined): Promise<void> => {
  log('ContatoFidelizacaoModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.id) {
    // Edit mode
    await openForEdit(data.id);
  } else if (data?.franquiaId && data?.clienteId) {
    // New mode with context
    await openForNew(data.franquiaId, data.clienteId, data.clienteNome || '', data.franquiaNome || '');
  }
};

// Listen to eventBus for opening modal
onMounted(() => {
  log('ContatoFidelizacaoModal', 'onMounted', 'Component mounted, setting up event listeners');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.contato-fidelizacao.open', handleModalOpen);
  eventBus.on('modal.contato-fidelizacao.close', handleClose);

  // Legacy events (for backward compatibility)
  eventBus.on('contato-fidelizacao.open-modal-new', handleOpenModalNew);
  eventBus.on('contato-fidelizacao.open-modal-edit', handleOpenModalEdit);

  log('ContatoFidelizacaoModal', 'onMounted', 'Event listeners registered successfully');
});

onBeforeUnmount(() => {
  log('ContatoFidelizacaoModal', 'beforeUnmount', 'Cleaning up event listeners');

  // Remove standard modal events
  eventBus.off('modal.contato-fidelizacao.open', handleModalOpen);
  eventBus.off('modal.contato-fidelizacao.close', handleClose);

  // Remove legacy events
  eventBus.off('contato-fidelizacao.open-modal-new', handleOpenModalNew);
  eventBus.off('contato-fidelizacao.open-modal-edit', handleOpenModalEdit);
});

// Expose public methods
defineExpose({
  openForNew,
  openForEdit
});
</script>

<style scoped>
/* Add any modal-specific styles here */
</style>

